<template>
    <Drawer ref="drawer" @scroll="onScroll" @collapsed="$emit('collapsed', $event)">
        <div class="book-nav">
            <div v-if="reviewerRail" class="book-nav__reviewer-header">
                <span class="book-nav__reviewer-title">{{ $t('editor.contents.chapters') }}</span>
                <button
                    type="button"
                    class="book-nav__reviewer-toggle"
                    :title="$t('editor.contents.toggleChapters')"
                    :aria-label="$t('editor.contents.toggleChapters')"
                    @click="$refs.drawer.toggle()"
                >
                    <IconChevronOutline direction="left" />
                </button>
            </div>
            <div v-if="!reviewerRail" class="book-nav__tabs">
                <button
                    v-for="tab in tabs"
                    :key="tab"
                    v-t="tab"
                    :class="{ 'is-active': tab === selectedTab }"
                    @click.prevent="selectTab(tab)"
                />
            </div>
            <div class="book-nav__tabs-content">
                <div v-show="selectedTab === 'editor.contents.title'">
                    <ul v-if="contents.length" class="book-nav__list">
                        <EditorNavigationItem
                            v-if="canLoadSectionDuration && hasTextBeforeFirstChapter"
                            class="book-nav__item--disabled"
                            :node="{
                                raw_text: $t('editor.contents.sectionDuration.beforeChapter'),
                                type_text: 'default',
                                children: []
                            }"
                            :is-active="false"
                            :active-id="active.id"
                            :reviewer-rail="reviewerRail"
                            :section-info="sectionsDuration.sections[0]"
                        />

                        <EditorNavigationItem
                            v-for="(node, index) in contents"
                            :key="node.id"
                            :node="node"
                            :is-active="isActive(node)"
                            :active-id="active.id"
                            :reviewer-rail="reviewerRail"
                            :section-info="getSectionDuration(index)"
                            @click="onNavClick"
                        />
                    </ul>
                    <div v-if="!contents.length" class="book-nav__notice">
                        {{ $t('editor.contents.notice') }}
                        <ol class="book-nav__tutorial">
                            <li>
                                {{ $t('editor.contents.step1') }}
                            </li>
                            <li>
                                {{ $t('editor.contents.step2') }}
                                <img
                                    class="book-nav__tutorial-image"
                                    src="@speechki-frontend/shared/assets/img/nav-tutorial-1.jpg"
                                />
                            </li>
                            <li>
                                {{ $t('editor.contents.step3') }}
                                <img
                                    class="book-nav__tutorial-image"
                                    src="@speechki-frontend/shared/assets/img/nav-tutorial-2.jpg"
                                />
                            </li>
                            <li>{{ $t('editor.contents.step4') }}</li>
                        </ol>
                    </div>
                </div>
                <div v-show="stylesheetVisibility && selectedTab === 'stylesheet.menuItem'">
                    <StylesheetList
                        ref="stylesheetRes"
                        @styleSheetItemClick="$emit('styleSheetItemClick', $event)"
                    />
                </div>
            </div>
        </div>
    </Drawer>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import EditorNavigationItem from '../EditorNavigationItem/EditorNavigationItem';
import StylesheetList from '../StylesheetList/StylesheetList';
import Drawer from '@speechki-frontend/shared/ui/Drawer';
import { IconChevronOutline } from '@speechki-frontend/shared/ui/icons';

import { fetchSectionsDuration } from '../../api/books';

export default {
    name: 'EditorNavigation',
    emits: ['change', 'collapsed', 'styleSheetItemClick'],
    components: {
        Drawer,
        EditorNavigationItem,
        StylesheetList,
        IconChevronOutline
    },

    props: {
        reviewerRail: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            active: {},
            sectionsDuration: undefined,
            collapsed: false,
            tabs: ['editor.contents.title'],
            selectedTab: 'editor.contents.title',
            search: '',
            stylesheetIsLoaded: false
        };
    },

    computed: {
        ...mapState({
            book: state => state.book.book
        }),
        ...mapGetters(['isGuestAccess']),
        ...mapGetters('guestAccess', ['isInGroup', 'guestAccessActionPermission']),
        ...mapState({
            contents: state => state.book.contents
        }),
        id() {
            return Number(this.$route.params.id);
        },
        hasTextBeforeFirstChapter() {
            return (
                this.sectionsDuration &&
                this.sectionsDuration.sections.length &&
                this.sectionsDuration.sections.length !== this.contents.length
            );
        },
        canLoadSectionDuration() {
            if (this.$isManageApp) return true;

            if (this.isGuestAccess) {
                return this.isInGroup('editor') || this.isInGroup('main_editor');
            }

            return false;
        },
        stylesheetVisibility() {
            if (!this.isGuestAccess) return true;
            return this.guestAccessActionPermission('style_rule', 'list');
        }
    },

    watch: {
        contents: {
            immediate: true,
            handler() {
                if (this.contents.length) {
                    this.fetchSectionsDuration();
                }
            }
        },
        collapsed(value) {
            if (value) localStorage.setItem('navigation-collapsed', Date.now());
            else localStorage.removeItem('navigation-collapsed');
        }
    },

    mounted() {
        this.collapsed = localStorage.getItem('navigation-collapsed') !== null;

        let collapsed = localStorage.getItem('navigation-collapsed') !== null;
        if (this.reviewerRail) {
            this.collapsed = false;
            this.$refs.drawer.open();
        } else if (collapsed || this.isReviewer) {
            this.$refs.drawer.collapse();
        }

        // reviewerRail has no tab buttons (the stylesheet tab isn't reachable
        // there), so it must never restore a persisted 'stylesheet.menuItem'
        // selection — that would strand the reviewer on the stylesheet list
        // with no way back to the contents rail.
        if (this.reviewerRail) {
            this.selectedTab = 'editor.contents.title';
        } else {
            const selectedTab = localStorage.getItem('editorNavigationTab');
            if (selectedTab) this.selectedTab = selectedTab;
        }
    },

    created() {
        if (this.stylesheetVisibility) {
            this.tabs.push('stylesheet.menuItem');
        }

        this.fetchContents(this.$route.params.id);
    },

    methods: {
        ...mapActions(['fetchContents']),

        onNavClick({ id, page }) {
            if (this.active.id && this.active.id == id) return;

            this.setActivePart({ id });

            this.active = {
                id: id
            };
            this.$emit('change', { id, page: Number(page) });
        },

        setActivePart(part) {
            this.active = {
                id: part.id
            };
        },

        // Master (non-reviewerRail) behavior: a whole top-level branch
        // highlights together — children inherit the parent's isActive
        // rather than matching by id individually. reviewerRail keeps the
        // newer per-node activeId matching in EditorNavigationItem instead.
        isActive(part) {
            return this.active.id === part.id;
        },

        async fetchSectionsDuration() {
            if (!this.canLoadSectionDuration) return;
            try {
                this.sectionsDuration = await fetchSectionsDuration(this.id);
            } catch (e) {
                console.log(e);
            }
        },

        getSectionDuration(chapterIndex) {
            if (
                !this.sectionsDuration ||
                !this.sectionsDuration.sections ||
                !this.sectionsDuration.sections.length
            )
                return;

            const offset = this.sectionsDuration.sections.length - this.contents.length;

            return this.sectionsDuration.sections[chapterIndex + offset];
        },
        collapse() {
            this.collapsed = false;
        },
        collapseToggle() {
            this.collapsed = !this.collapsed;
        },
        selectTab(tab) {
            this.selectedTab = tab;
            localStorage.setItem('editorNavigationTab', tab);
        },
        onScroll(event) {
            this.$refs.stylesheetRes.scroll(event);
        }
    }
};
</script>

<style lang="scss" src="./EditorNavigation.scss"></style>
